import React, { useRef, useState, useCallback } from 'react';
import { Move, ArrowUp, AlignVerticalJustifyCenter, ArrowDown } from 'lucide-react';
import { absoluteUrl } from '../lib/api';

/**
 * ImagePositionPicker — lets the admin choose the focal point (CSS
 * object-position) of an image and see, in a live aspect-video frame, exactly
 * how it will be cropped in the card/hero. Drag the dot or use the presets.
 *
 * value: string like "50% 30%" or a keyword ("center"). onChange(string).
 */
function parsePos(value) {
  if (!value || value === 'center') return { x: 50, y: 50 };
  const m = String(value).match(/(-?\d+(?:\.\d+)?)%\s+(-?\d+(?:\.\d+)?)%/);
  if (m) return { x: parseFloat(m[1]), y: parseFloat(m[2]) };
  if (value === 'top') return { x: 50, y: 0 };
  if (value === 'bottom') return { x: 50, y: 100 };
  return { x: 50, y: 50 };
}

export default function ImagePositionPicker({ value, imageUrl, onChange, testid = 'image-position-picker' }) {
  const frameRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const pos = parsePos(value);

  const updateFromEvent = useCallback((clientX, clientY) => {
    const el = frameRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = Math.min(100, Math.max(0, ((clientX - rect.left) / rect.width) * 100));
    const y = Math.min(100, Math.max(0, ((clientY - rect.top) / rect.height) * 100));
    onChange(`${Math.round(x)}% ${Math.round(y)}%`);
  }, [onChange]);

  if (!imageUrl) return null;
  const objectPosition = `${pos.x}% ${pos.y}%`;

  return (
    <div data-testid={testid} className="space-y-2">
      <div className="flex items-center gap-1.5 text-xs text-charcoal-500">
        <Move size={12} /> Arrastra el punto sobre la imagen para elegir qué parte se mostrará en la tarjeta.
      </div>
      <div
        ref={frameRef}
        data-testid={`${testid}-frame`}
        className="relative w-full max-w-md aspect-video rounded-xl overflow-hidden bg-gray-100 cursor-crosshair select-none ring-1 ring-gray-200"
        onMouseDown={(e) => { setDragging(true); updateFromEvent(e.clientX, e.clientY); }}
        onMouseMove={(e) => { if (dragging) updateFromEvent(e.clientX, e.clientY); }}
        onMouseUp={() => setDragging(false)}
        onMouseLeave={() => setDragging(false)}
        onTouchStart={(e) => { const t = e.touches[0]; updateFromEvent(t.clientX, t.clientY); }}
        onTouchMove={(e) => { const t = e.touches[0]; updateFromEvent(t.clientX, t.clientY); }}
      >
        <img
          src={absoluteUrl(imageUrl)}
          alt="Previsualización del encuadre"
          className="w-full h-full object-cover pointer-events-none"
          style={{ objectPosition }}
          draggable={false}
        />
        {/* Focal-point marker */}
        <div
          className="absolute w-6 h-6 -ml-3 -mt-3 rounded-full border-2 border-white shadow-lg ring-2 ring-cmic-500 bg-cmic-500/30 pointer-events-none"
          style={{ left: `${pos.x}%`, top: `${pos.y}%` }}
        />
        <span className="absolute bottom-1.5 left-1.5 text-[10px] font-medium text-white bg-black/50 px-1.5 py-0.5 rounded">
          Vista de tarjeta (16:9)
        </span>
      </div>
      <div className="flex items-center gap-1.5">
        <span className="text-xs text-charcoal-500 mr-1">Encuadre rápido:</span>
        {[
          { label: 'Arriba', val: '50% 0%', Icon: ArrowUp },
          { label: 'Centro', val: '50% 50%', Icon: AlignVerticalJustifyCenter },
          { label: 'Abajo', val: '50% 100%', Icon: ArrowDown },
        ].map(({ label, val, Icon }) => {
          const active = objectPosition === val;
          return (
            <button
              key={label}
              type="button"
              data-testid={`${testid}-preset-${label.toLowerCase()}`}
              onClick={() => onChange(val)}
              className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-lg border transition-colors ${active ? 'bg-cmic-600 text-white border-cmic-600' : 'bg-white text-charcoal-600 border-gray-200 hover:bg-gray-50'}`}
            >
              <Icon size={12} /> {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
